import React, { ElementType, SyntheticEvent, useState } from 'react';

import { useLocation, useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Box, Stack, Tab, Tabs } from '@mui/material';

import PageSubheader from '../Common/Elements/PageSubheader';
import { titleCase, useRegisterSpotlightRoutes } from '../Common/Spotlight/SpotlightProvider';

type Props = {
  items: Array<{
    title: string;
    component: ElementType;
    slug?: string;
    componentProps?: Record<string, unknown>;
    sortKey?: string;
  }>;
  defaultItem: string;
  sort?: boolean;
};

export default function ContentWithSidebar({ items, defaultItem, sort = true }: Props) {
  // `report` is the Finance route's leaf param; `subsection` is the equivalent
  // under /commercial-ops/:section/:subsection.
  const { section, report, subsection } = useParams<{
    section: string;
    report?: string;
    subsection?: string;
  }>();
  const { pathname } = useLocation();

  // Derive base path from the segments before :section, matching the first
  // occurrence so a leaf slug equal to the section slug cannot shift it.
  // e.g. "/finance/bank-files/citi-dom-file" → basePath="/finance", section="bank-files"
  const segments = pathname.split('/').filter(Boolean);
  const sectionIndex = segments.indexOf(section);
  const basePath = sectionIndex > 0 ? `/${segments.slice(0, sectionIndex).join('/')}` : '';
  const parentLabel = titleCase(basePath.split('/').filter(Boolean).pop() ?? '');
  const tabLabel = titleCase(section);

  useRegisterSpotlightRoutes(
    items.map((item) => ({
      path: `${basePath}/${section}/${item.slug ?? item.title}`,
      label: `${parentLabel} / ${tabLabel} / ${item.title}`,
      section: parentLabel,
    })),
  );

  const [current, setCurrent] = useState(report ?? subsection ?? defaultItem);

  const currentItem = items.find((item) => (item.slug ? item.slug === current : item.title === current));

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    setCurrent(newValue);
  };

  const tabs = [...items];
  if (sort) {
    tabs.sort((itemA, itemB) => (itemA.sortKey ?? itemA.title).localeCompare(itemB.sortKey ?? itemB.title));
  }

  return (
    <Stack direction="row" spacing={4}>
      <Tabs
        orientation="vertical"
        value={current}
        onChange={handleChange}
        sx={{ borderRight: 1, borderColor: 'divider' }}
      >
        {tabs.map((tab, index) => {
          return (
            <Tab
              key={tab.slug ?? index}
              label={tab.title}
              value={tab.slug ?? tab.title}
              sx={{
                alignItems: 'start',
                color: 'text.secondary',
                textAlign: 'left',
                '&.Mui-selected': {
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                },
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                },
              }}
              {...(tab.slug ? { component: Link, to: `${basePath}/${section}/${tab.slug}` } : {})}
            />
          );
        })}
      </Tabs>

      <Box flex={1}>
        {currentItem && (
          <>
            <PageSubheader title={currentItem.title} />
            <Box component={currentItem.component} {...(currentItem.componentProps ?? {})} />
          </>
        )}
      </Box>
    </Stack>
  );
}
